/**
 * @file useSectionElementWarnings.ts
 * @description Предупреждения об элементах формы секции (phase19.16, партия
 * SB120, этап 3а; uiux-sb120-stage3 §2, §4; db-design-sb120-stage3 §6):
 * `sectionElementWarnings` shared по форме (не по сохранённому блоку) —
 * «!» / «✕» у группы и строка в списке замечаний меняются сразу.
 *
 *  - «Свой шрифт удалён» (`element_font_missing`) — только когда список
 *    своих шрифтов магазина получен: до загрузки любой `shop:` выглядел бы
 *    удалённым;
 *  - «больше 3 шрифтов» считает тему (шапка и подвал своих шрифтов не
 *    имеют — пишутся ролями темы) и свои шрифты элементов остальных блоков
 *    страницы (`pageFontKeys`, сохранённые payload из состояния конструктора).
 */

import { sectionElementWarnings, sectionFontKeys, type SectionElementWarning, type SectionPayload, type SiteTheme } from "@chaiberi/shared";
import { useMemo } from "react";

import { useCanvasFontRegistry, useShopFonts } from "../../components/site-view/shop-fonts-context";

export function useSectionElementWarnings(
  payload: SectionPayload,
  theme: SiteTheme | null,
  /** Сохранённые секции остальных блоков страницы (без редактируемого). */
  otherSections: readonly SectionPayload[] = [],
): SectionElementWarning[] {
  const loaded = useCanvasFontRegistry();
  const { registry } = useShopFonts();
  const active = loaded ?? registry;
  // Удалённый свой шрифт другого блока на сайте не грузится — не считаем.
  const pageFontKeys = useMemo(() => sectionFontKeys(otherSections).filter((key) => active.has(key)), [otherSections, active]);
  return useMemo(() => {
    try {
      const found = sectionElementWarnings(payload, active, theme ? { theme, pageFontKeys } : {});
      return loaded ? found : found.filter((w) => w.code !== "element_font_missing");
    } catch {
      // Подсказка, а не проверка: необычная форма не должна ронять панель.
      return [];
    }
  }, [payload, theme, loaded, active, pageFontKeys]);
}
